import React, { useRef } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaPrint } from "react-icons/fa";

const OrderRequestReceipt = ({ order }) => {
  const printRef = useRef();

  const handlePrint = () => {
    if (!order) return;

    const doc = new jsPDF("p", "mm", "a4");
    const margin = 16;
    const leMargin = 10;
    const lineSpacing = 6;
    let y = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setDrawColor(0); // Black border
    doc.rect(leMargin, leMargin, pageWidth - 2 * leMargin, pageHeight - 2 * leMargin);

    const logo = new Image();
    logo.src = "/logo.png";

    logo.onload = () => {
      // Add Logo
      doc.addImage(logo, "PNG", margin, y, 30, 30);

      // Header
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Order Request Receipt", 105, y + 10, { align: "center" });

      y += 35;

      // Order Info
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Order Request Code:", margin, y);

      const labelWidth1 = doc.getTextWidth("Order Request Code: ");

      doc.setFont("helvetica", "normal");
      doc.text(order.orderRequestCode, margin + labelWidth1, y);
      y += lineSpacing;

      doc.setFont("helvetica", "bold");
      doc.text("Customer Name:", margin, y);

      const labelWidth2 = doc.getTextWidth("Customer Name: ");

      doc.setFont("helvetica", "normal");
      doc.text(order.customer_name, margin + labelWidth2, y);
      y += lineSpacing;

      const locationUrl = `https://www.google.com/maps?q=${order.latitude},${order.longitude}`;

      // "Pickup Location:" in black
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Pickup Location:", margin, y);

      // Get width of the label to offset the link correctly
      const labelWidth = doc.getTextWidth("Pickup Location:");

      // Draw the location name as a blue clickable link
      doc.setTextColor(0, 0, 255);
      doc.textWithLink(order.location_name, margin + labelWidth + 2, y, { url: locationUrl });

      // Reset text color to black for further content
      doc.setTextColor(0, 0, 0);

      y += lineSpacing;

      doc.text("Total Amount:", margin, y);

      const labelWidth3 = doc.getTextWidth("Total Amount: ");

      doc.setFont("helvetica", "normal");
      doc.text(` LKR ${order.totalOrderPrice?.toFixed(2) || "0.00"}`, margin + labelWidth3, y);
      y += lineSpacing + 4;
      
      const rows = (order.orderItems || []).map((item, index) => ({
        index: index + 1,
        product_code: item.product_code,
        product_name: item.product_name,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price?.toFixed(2) || "0.00",
        total_price: item.total_price?.toFixed(2) || "0.00",
      }));
      
      autoTable(doc, {
        startY: y,
        head: [[ "#", "Product Code", "Product Name", "Quantity", "Unit", "Unit Price (LKR)", "Total (LKR)" ]],
        body: rows.map(row => [
          row.index,
          row.product_code,
          row.product_name,
          row.quantity,
          row.unit,
          row.unit_price,
          row.total_price
        ]),
        styles: {
          fontSize: 10,
          cellPadding: 2,
          halign: "left", // 🛠 base alignment for both head and body
        },
        headStyles: {
          fillColor: [220, 220, 220],
          textColor: 0,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "center" },
          2: { halign: "left" },   // corrected this to "left" for Product Name
          3: { halign: "right" },
          4: { halign: "center" },
          5: { halign: "right" },
          6: { halign: "right" },
        }
      });
      
      const finalY = doc.lastAutoTable.finalY || y + 30;

      // Footer
      doc.setFontSize(10);
      doc.text("Thank you for your business!", 105, finalY + 10, { align: "center" });
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 105, finalY + 15, { align: "center" });

      // Save PDF
      doc.save(`${order.orderRequestCode}_Receipt.pdf`);
    };
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center p-4 sm:p-6">
      <div ref={printRef} className="w-full max-w-3xl bg-white shadow-md rounded-lg p-4 sm:p-6">
        <div className="border-2 border-black p-4 rounded-md">
          {/* Logo and Header */}
          <div className="flex flex-wrap justify-between items-center mb-4">
            <img src="/agro_farm_ims_logo.png" alt="Logo" className="w-20 h-20 sm:w-28 sm:h-28" />
            <h2 className="text-lg sm:text-xl font-bold">Order Request Receipt</h2>
          </div>

          {/* Order Details */}
          <div className="mb-6 text-sm sm:text-base">
            <p><strong>Order Request Code:</strong> {order.orderRequestCode}</p>
            <p><strong>Customer Name:</strong> {order.customer_name}</p>
            <p>
              <strong>Pickup Location Name:</strong>{" "}
              <a
                href={`https://www.google.com/maps?q=${order.latitude},${order.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                {order.location_name}
              </a>
            </p>
            <p><strong>Total Amount:</strong> LKR {order.totalOrderPrice.toFixed(2)}</p>
            {/* <div className="flex items-center mt-2">
              <p className="font-bold mr-2">Status:</p>
              <span className="inline-block px-3 py-1 rounded-md bg-yellow-500 text-white font-semibold text-xs uppercase">
                {order.status}
              </span>
            </div> */}
          </div>

          {/* Order Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 mb-6 text-xs sm:text-sm">
              <thead>
                <tr className="bg-gray-300">
                  <th className="border border-gray-300 p-2">#</th>
                  <th className="border border-gray-300 p-2">Product Code</th>
                  <th className="border border-gray-300 p-2">Item</th>
                  <th className="border border-gray-300 p-2">Product Name</th>
                  <th className="border border-gray-300 p-2 text-right">Quantity</th>
                  <th className="border border-gray-300 p-2 text-center">Unit</th>
                  <th className="border border-gray-300 p-2 text-right">Unit Price (LKR)</th>
                  <th className="border border-gray-300 p-2 text-right">Total Price (LKR)</th>
                </tr>
              </thead>
              <tbody>
                {order.orderItems.map((item, index) => (
                  <tr key={index}>
                    <td className="border border-gray-300 p-2 text-center">{index + 1}</td>
                    <td className="border border-gray-300 p-2 text-center">{item.product_code}</td>
                    <td className="border border-gray-300 p-2 text-center">
                      <img
                        src={`http://192.168.2.48:5000${item.product_image}`}
                        alt={item.product_name}
                        className="w-12 h-12 object-cover mx-auto"
                      />
                    </td>
                    <td className="border border-gray-300 p-2 text-center">{item.product_name}</td>
                    <td className="border border-gray-300 p-2 text-right">{item.quantity}</td>
                    <td className="border border-gray-300 p-2 text-center">{item.unit}</td>
                    <td className="border border-gray-300 p-2 text-right">{item.unit_price.toFixed(2)}</td>
                    <td className="border border-gray-300 p-2 text-right">{item.total_price.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="text-center mt-4">
            <p className="text-xs sm:text-sm text-gray-600">Thank you for your business!</p>
            <p className="text-xs sm:text-sm text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Print Button */}
      <div className="mt-6 flex justify-center">
        <button
          onClick={handlePrint}
          className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 flex items-center"
        >
          <FaPrint className="mr-2" /> Print
        </button>
      </div>
    </div>
  );
};

export default OrderRequestReceipt;
